import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, ChevronDownIcon, ChevronRightIcon, CloseIcon } from './Icons';
import Pagination from './Pagination';
import CallButton from './CallButton';
import DateRangePicker from './DateRangePicker';
import { getKnownAgents, normalizeNumber, localDateStr } from './callUtils';

// ParcelX tab of the NDR page (Ndr.js renders it when the provider switch is
// on ParcelX). Same live-fetch idea as the Jiffy tab: ParcelX is the system
// of record, the server (server/ivr/parcelxNdr.js) caches its answer for 30s
// across every agent's tab, so the poll matches that.
const DEFAULT_PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 30000;
const FOLLOW_UP_MS = 5000;
const DEFAULT_RANGE_DAYS = 30;

// ParcelX's ndr_type, shown as the same Active / Initiated tabs as ParcelX's
// own NDR panel. Its panel also has "Closed", but the API rejects it ("NDR
// TYPE must be either ACTIVE or INITIATED"), so it can't be offered here.
const NDR_TYPE_TABS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INITIATED', label: 'Initiated' },
];

// Tabs from ParcelX's shipment status code (its documented status table).
const RTO_CODES = ['224', '225', '234', '235', '236'];
// Shipments ParcelX can no longer re-attempt: delivered, returned/returning,
// cancelled, lost or disposed.
const FINAL_CODES = ['226', '227', '237', '243', ...RTO_CODES];

const BUCKETS = [
  { key: 'all', label: 'All', cardClass: '' },
  { key: 'ndr', label: 'In NDR', cardClass: 'critical' },
  { key: 'rto', label: 'RTO', cardClass: '' },
  { key: 'delivered', label: 'Delivered', cardClass: 'answered' },
  { key: 'other', label: 'Other / Unknown', cardClass: '' },
];

const ndrBucket = (ndr) => {
  const code = ndr.status_code;
  if (code === '233') return 'ndr';
  if (RTO_CODES.includes(code)) return 'rto';
  if (code === '226') return 'delivered';
  return 'other';
};

const statusClass = (ndr) => {
  const bucket = ndrBucket(ndr);
  if (bucket === 'delivered') return 'answered';
  if (bucket === 'rto') return 'attempted';
  if (bucket === 'ndr') return 'missed';
  return 'viewing';
};

// A row whose status couldn't be looked up stays actionable; ParcelX itself
// will refuse the action if the shipment can't take it.
const canTakeAction = (ndr) => !FINAL_CODES.includes(ndr.status_code);
const FINAL_STATUS_TITLE = 'Delivered, returned, cancelled or lost: ParcelX can no longer act on this shipment';

// ParcelX sends "YYYY-MM-DD HH:mm:ss" with no zone; shown as-is, just
// reformatted (no timezone shifting), same as the Jiffy tab.
const parseParcelxDate = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const parsed = parseParcelxDate(value);
  if (!parsed) return '-';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  const parsed = parseParcelxDate(value);
  if (!parsed) return '-';
  return parsed.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatRupees = (value) => {
  const amount = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(amount)) return '-';
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const formatAddress = (address) =>
  [address?.line1, address?.line2, address?.city, address?.state, address?.pincode].filter(Boolean).join(', ');

const productNames = (ndr) => (Array.isArray(ndr.products) ? ndr.products.join(', ') : '');

// "NDR: 1 · OFD: 1", counted from the shipment's tracking history, as in
// ParcelX's panel. Empty until tracking has loaded.
const attemptsLabel = (ndr) =>
  ndr.ndr_attempts == null && ndr.ofd_attempts == null ? '' : `NDR: ${ndr.ndr_attempts ?? 0} · OFD: ${ndr.ofd_attempts ?? 0}`;

// ParcelX's own status codes for "this scan was a failed delivery" and
// "out for delivery", for colouring the tracking timeline.
const NDR_CODE = '233';
const OFD_CODE = '228';

const defaultRange = () => {
  const start = new Date();
  start.setDate(start.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { from: localDateStr(start), to: localDateStr(new Date()) };
};

function ParcelxDetails({ ndr }) {
  const fields = [
    ['Address', formatAddress(ndr.address)],
    ['Alternate phone', ndr.customer_alt_phone],
    ['Email', ndr.customer_email],
    ['Order date', formatDateTime(ndr.order_date)],
    ['Picked up', formatDateTime(ndr.pickup_date)],
    ['NDR raised', formatDateTime(ndr.ndr_time)],
    ['Out for delivery', ndr.ofd_time ? formatDateTime(ndr.ofd_time) : ''],
    ['ParcelX order ID', ndr.shipment_id],
    [ndr.channel_name ? `${ndr.channel_name} order ID` : 'Channel order ID', ndr.channel_order_id],
    ['Invoice', ndr.invoice_number],
    ['Shipping mode', ndr.express_type],
    ['Products', productNames(ndr)],
    ['SKUs', Array.isArray(ndr.skus) ? ndr.skus.join(', ') : ''],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '' && value !== '-');
  const events = Array.isArray(ndr.tracking_events) ? ndr.tracking_events : [];

  return (
    <div className="ndr-history">
      {ndr.details_status === 'error' && (
        <div className="call-feedback call-feedback-error">
          Couldn't load this shipment's details (customer, payment, status) from ParcelX. They'll be retried on a later refresh.
        </div>
      )}
      {(ndr.details_status === 'pending' || ndr.tracking_status === 'pending') && (
        <div className="ndr-attempt-card-empty">Some shipment details are still loading from ParcelX; they'll appear on a later refresh.</div>
      )}
      <div className="ndr-history-meta">
        {fields.map(([label, value]) => (
          <div key={label}>
            <span className="ndr-history-meta-label">{label}</span>
            {String(value)}
          </div>
        ))}
      </div>

      <div className="ndr-history-meta-label">Tracking history</div>
      {ndr.tracking_status === 'error' ? (
        <div className="ndr-attempt-card-empty">Couldn't load tracking from ParcelX. It'll be retried on a later refresh.</div>
      ) : events.length === 0 ? (
        <div className="ndr-attempt-card-empty">No tracking events yet.</div>
      ) : (
        <ol className="ndr-timeline">
          {events.map((event, index) => {
            const code = String(event.status_code);
            return (
              <li
                key={`${code}-${event.event_date}-${index}`}
                className={`ndr-timeline-item ${code === NDR_CODE ? 'failed' : code === OFD_CODE ? 'actioned' : ''}`}
              >
                <div className="ndr-timeline-head">
                  <span className="ndr-timeline-action">{event.status_title || '-'}</span>
                  {event.status_location && <span className="ndr-timeline-by">{event.status_location}</span>}
                  <span className="ndr-timeline-time">{formatDateTime(event.event_date)}</span>
                </div>
                {event.status_description && <div className="ndr-timeline-remarks">{event.status_description}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ParcelX's ndr-action values, with the same preset reasons as the Jiffy
// tab; the chosen reason is sent to ParcelX as the action's `remark`.
const ACTION_INFO = {
  REATTEMPT: {
    label: 'Re-attempt',
    title: 'Re-attempt delivery (ParcelX)',
    button: 'Request re-attempt',
    note: 'ParcelX will ask the courier to try delivery again.',
    reasons: [
      'Customer requested another delivery attempt',
      'Customer is available now, please deliver',
      'Customer wants delivery on a later date',
      'Address / landmark confirmed with customer',
      'Customer phone is reachable now',
      'Customer will keep COD amount ready',
    ],
  },
  RTO: {
    label: 'RTO',
    title: 'Return to origin (ParcelX)',
    button: 'Mark as RTO',
    note: 'ParcelX will have the courier send these shipments back to the warehouse. This cannot be undone from here.',
    reasons: [
      'Customer refused delivery',
      'Customer cancelled the order',
      'Customer not reachable after multiple attempts',
      'Wrong / incomplete address',
      'Customer does not have COD amount',
    ],
  },
};
const OTHER_REASON = '__other__';

// Re-attempt or RTO for one or more ParcelX shipments. For a re-attempt, a
// new address/phone can be sent for a single shipment and a preferred date
// for any number; RTO takes only the reason.
function ParcelxActionModal({ ndrs, initialAction = 'REATTEMPT', onClose, onDone }) {
  const { authFetch } = useAuth();
  const [action, setAction] = useState(initialAction);
  const [reasonChoice, setReasonChoice] = useState(ACTION_INFO[initialAction].reasons[0]);
  const [customReason, setCustomReason] = useState('');
  const [reattemptDate, setReattemptDate] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [failures, setFailures] = useState(null);

  const info = ACTION_INFO[action];
  const isReattempt = action === 'REATTEMPT';
  const isBulk = ndrs.length > 1;
  const remarks = (reasonChoice === OTHER_REASON ? customReason : reasonChoice).trim();
  const today = localDateStr(new Date());

  const switchAction = (next) => {
    setAction(next);
    setReasonChoice(ACTION_INFO[next].reasons[0]);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!remarks) {
      setError('Pick or type a reason.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const response = await authFetch('/api/ivr/ndr/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'parcelx',
          action,
          remarks,
          reattempt_date: isReattempt && reattemptDate ? reattemptDate : undefined,
          address: isReattempt && !isBulk && address.trim() ? address.trim() : undefined,
          phone: isReattempt && !isBulk && phone.trim() ? phone.trim() : undefined,
          items: ndrs.map((ndr) => ({ awb_number: ndr.awb_number, order_number: ndr.order_number })),
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || `ParcelX did not accept this ${info.label}.`);

      const successful = Array.isArray(body?.successful) ? body.successful : [];
      const failed = Array.isArray(body?.failed) ? body.failed : [];
      const what = `${info.label} sent to ParcelX for ${successful.length} shipment${successful.length === 1 ? '' : 's'}`;
      if (failed.length === 0) {
        onDone(`${what}.`, successful);
        return;
      }
      setFailures(failed);
      if (successful.length > 0) onDone(`${what}; ${failed.length} failed.`, successful, { keepOpen: true });
    } catch (err) {
      setError(err.message || `ParcelX did not accept this ${info.label}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const orderLabel = (awb) => {
    const ndr = ndrs.find((row) => String(row.awb_number) === String(awb));
    return ndr?.order_number || awb;
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={submitting ? undefined : onClose}>
      <div className="modal-panel modal-panel-compose" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{isBulk ? `Bulk update · ${ndrs.length} ParcelX shipments` : info.title}</h2>
            {!isBulk && (
              <p>
                Order {ndrs[0].order_number || '-'} · AWB {ndrs[0].awb_number} · {ndrs[0].customer_name || 'Unknown customer'}
              </p>
            )}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close" disabled={submitting}>
            <CloseIcon width={18} height={18} />
          </button>
        </div>

        {failures ? (
          <div className="modal-body">
            <div className="login-error">
              {failures.length} of {ndrs.length} shipment{ndrs.length === 1 ? '' : 's'} were not updated by ParcelX:
            </div>
            <ul className="ndr-bulk-failures">
              {failures.map((failure) => (
                <li key={failure.awb_number}>
                  <strong>{orderLabel(failure.awb_number)}</strong> (AWB {failure.awb_number}) — {failure.message}
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn-primary login-submit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="modal-body" onSubmit={handleSubmit}>
            <div className="ndr-action-switch" role="radiogroup" aria-label="Action">
              {Object.entries(ACTION_INFO).map(([key, value]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={action === key}
                  className={`ndr-action-switch-option ${key === 'RTO' ? 'danger' : ''} ${action === key ? 'active' : ''}`}
                  onClick={() => switchAction(key)}
                >
                  {value.label}
                </button>
              ))}
            </div>
            <p className={isReattempt ? 'ndr-action-note' : 'ndr-action-note danger'}>{info.note}</p>

            <div className="login-field">
              <span>Reason *</span>
              <div className="ndr-reason-list">
                {[...info.reasons, OTHER_REASON].map((reason) => (
                  <label key={reason} className={`ndr-reason ${reasonChoice === reason ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="parcelx-ndr-reason"
                      checked={reasonChoice === reason}
                      onChange={() => setReasonChoice(reason)}
                    />
                    <span>{reason === OTHER_REASON ? 'Other (type a reason)' : reason}</span>
                  </label>
                ))}
              </div>
              {reasonChoice === OTHER_REASON && (
                <textarea
                  rows={2}
                  value={customReason}
                  onChange={(event) => setCustomReason(event.target.value)}
                  maxLength={500}
                  placeholder="Reason sent to ParcelX"
                  autoFocus
                />
              )}
            </div>

            {isReattempt && (
              <label className="login-field">
                <span>Re-attempt date (optional)</span>
                <input type="date" min={today} value={reattemptDate} onChange={(event) => setReattemptDate(event.target.value)} />
              </label>
            )}

            {isReattempt && !isBulk && (
              <>
                <label className="login-field">
                  <span>New delivery address (optional)</span>
                  <textarea
                    rows={2}
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    maxLength={500}
                    placeholder={formatAddress(ndrs[0].address) || 'Leave blank to keep the current address'}
                  />
                </label>
                <label className="login-field">
                  <span>New phone number (optional)</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder={ndrs[0].customer_phone || '10-digit mobile number'}
                  />
                </label>
              </>
            )}

            {isBulk && (
              <div className="login-field">
                <span>Shipments ({ndrs.length})</span>
                <ul className="ndr-bulk-orders">
                  {ndrs.map((ndr) => (
                    <li key={ndr.awb_number}>
                      <strong>{ndr.order_number || ndr.awb_number}</strong>
                      <span>{ndr.customer_name || '-'}</span>
                      <span className="stat-label">{ndr.ndr_reason || ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="btn btn-primary login-submit" disabled={submitting || !remarks}>
              {submitting ? 'Sending to ParcelX…' : isBulk ? `${info.button} · ${ndrs.length} shipments` : info.button}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ParcelxNdr({ calls = [], providerSwitch = null }) {
  const { authFetch } = useAuth();
  const [ndrType, setNdrType] = useState('ACTIVE');
  const [range, setRange] = useState(defaultRange);
  const [ndrs, setNdrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedAwb, setExpandedAwb] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [actionTarget, setActionTarget] = useState(null); // { ndrs: [ndr], action }
  const [actionNotice, setActionNotice] = useState('');
  const [selectedAwbs, setSelectedAwbs] = useState(() => new Set());
  // ParcelX's list call takes 25–60s. The server answers before it's done:
  // `pending` (nothing to show yet) or `refreshing` (an older list while a
  // new one loads), and the page asks again after FOLLOW_UP_MS.
  const [pending, setPending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const followUpTimer = useRef(null);
  // Ignores replies to an older filter that land after a newer one.
  const requestSeq = useRef(0);

  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);

  const clearFollowUp = () => {
    if (followUpTimer.current) clearTimeout(followUpTimer.current);
    followUpTimer.current = null;
  };

  // `keepRows`: a manual Refresh keeps the table on screen while ParcelX
  // answers, instead of swapping it for "Loading…".
  const fetchNdrs = async ({ silent = false, fresh = !silent, keepRows = false } = {}) => {
    const seq = (requestSeq.current += 1);
    clearFollowUp();
    if (!silent && !keepRows) setLoading(true);
    if (keepRows) setRefreshing(true);
    const params = new URLSearchParams({ provider: 'parcelx', ndr_type: ndrType, from_date: range.from, to_date: range.to });
    if (fresh) params.set('refresh', '1');
    try {
      const response = await authFetch(`/api/ivr/ndr?${params.toString()}`);
      const body = await response.json().catch(() => null);
      if (seq !== requestSeq.current) return;
      if (!response.ok) {
        if (!silent && !keepRows) setNdrs([]);
        setPending(false);
        setRefreshing(false);
        setError(body?.error || 'Failed to fetch ParcelX NDRs.');
        return;
      }
      setError('');
      setPending(Boolean(body?.pending));
      setRefreshing(Boolean(body?.refreshing));
      if (!body?.pending) {
        setNdrs(Array.isArray(body?.ndrs) ? body.ndrs : []);
        setLastUpdated(body?.fetched_at ? new Date(body.fetched_at) : new Date());
      } else if (!silent && !keepRows) {
        // New filters with nothing loaded yet: don't leave the old filter's rows up.
        setNdrs([]);
        setLastUpdated(null);
      }
      if (body?.pending || body?.refreshing) {
        followUpTimer.current = setTimeout(() => fetchNdrs({ silent: true }), FOLLOW_UP_MS);
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      if (!silent && !keepRows) setNdrs([]);
      setPending(false);
      setRefreshing(false);
      setError(err.message || 'Failed to fetch ParcelX NDRs.');
    } finally {
      if (!silent && !keepRows && seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    setPending(false);
    setRefreshing(false);
    fetchNdrs({ fresh: false });
    const interval = setInterval(() => fetchNdrs({ silent: true }), AUTO_REFRESH_MS);
    return () => {
      clearInterval(interval);
      clearFollowUp();
      // A reply still in flight for these filters is ignored.
      requestSeq.current += 1;
    };
  }, [ndrType, range.from, range.to]);

  const bucketCounts = useMemo(() => {
    const counts = { all: ndrs.length, ndr: 0, rto: 0, delivered: 0, other: 0 };
    ndrs.forEach((ndr) => {
      counts[ndrBucket(ndr)] += 1;
    });
    return counts;
  }, [ndrs]);

  const filteredNdrs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ndrs.filter((ndr) => {
      if (bucketFilter !== 'all' && ndrBucket(ndr) !== bucketFilter) return false;
      if (!term) return true;
      return [
        ndr.order_number,
        ndr.awb_number,
        ndr.ndr_reason,
        ndr.customer_name,
        ndr.customer_phone,
        ndr.address?.city,
        ndr.address?.pincode,
        ndr.status_label,
        ndr.current_location,
        ndr.shipment_id,
        productNames(ndr),
        Array.isArray(ndr.skus) ? ndr.skus.join(' ') : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [ndrs, bucketFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [bucketFilter, search, ndrType, range.from, range.to]);

  const totalPages = Math.max(1, Math.ceil(filteredNdrs.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = filteredNdrs.slice((page - 1) * pageSize, page * pageSize);

  // Same rule as the Jiffy tab: only visible, actionable rows stay ticked.
  const selectableAwbs = useMemo(
    () => new Set(filteredNdrs.filter((ndr) => canTakeAction(ndr)).map((ndr) => String(ndr.awb_number))),
    [filteredNdrs]
  );
  useEffect(() => {
    setSelectedAwbs((prev) => {
      const next = new Set([...prev].filter((awb) => selectableAwbs.has(awb)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableAwbs]);

  const pageSelectable = pageRows.filter((ndr) => selectableAwbs.has(String(ndr.awb_number)));
  const pageAllSelected = pageSelectable.length > 0 && pageSelectable.every((ndr) => selectedAwbs.has(String(ndr.awb_number)));
  const pageSomeSelected = pageSelectable.some((ndr) => selectedAwbs.has(String(ndr.awb_number)));

  const toggleRow = (awb) => {
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      if (next.has(awb)) next.delete(awb);
      else next.add(awb);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      pageSelectable.forEach((ndr) => {
        if (pageAllSelected) next.delete(String(ndr.awb_number));
        else next.add(String(ndr.awb_number));
      });
      return next;
    });
  };

  const selectedNdrs = filteredNdrs.filter((ndr) => selectedAwbs.has(String(ndr.awb_number)));

  const handleActionDone = (message, successfulAwbs = [], { keepOpen = false } = {}) => {
    if (!keepOpen) setActionTarget(null);
    setActionNotice(message);
    setSelectedAwbs((prev) => {
      const next = new Set(prev);
      successfulAwbs.forEach((awb) => next.delete(String(awb)));
      return next;
    });
    fetchNdrs({ silent: true, fresh: true });
  };

  useEffect(() => {
    if (!actionNotice) return undefined;
    const timer = setTimeout(() => setActionNotice(''), 6000);
    return () => clearTimeout(timer);
  }, [actionNotice]);

  const columnCount = 13;

  return (
    <>
      <div className="incoming-filters-bar">
        {providerSwitch}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => fetchNdrs({ keepRows: true })}
          disabled={loading || pending || refreshing}
        >
          <RefreshIcon width={14} height={14} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        <input
          type="text"
          placeholder="Search by order no., customer, phone, AWB, reason…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="search-input"
        />
        <div className="customer-toggle" role="group" aria-label="ParcelX NDR type">
          {NDR_TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={ndrType === tab.value ? 'active' : ''}
              onClick={() => setNdrType(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <DateRangePicker
          fromDate={range.from}
          toDate={range.to}
          maxDate={localDateStr(new Date())}
          onApply={(from, to) => setRange(from && to ? { from, to } : defaultRange())}
        />
        {lastUpdated && (
          <span className="stat-label">
            Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            {refreshing ? ' · getting the latest from ParcelX…' : ''}
          </span>
        )}
      </div>

      <div className="stat-cards">
        {BUCKETS.map((bucket) => (
          <button
            key={bucket.key}
            type="button"
            className={`stat-card ${bucket.cardClass} ${bucketFilter === bucket.key ? 'active' : ''}`}
            onClick={() => setBucketFilter(bucket.key)}
          >
            <div className="stat-value">{bucketCounts[bucket.key]}</div>
            <div className="stat-label">{bucket.label}</div>
          </button>
        ))}
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}
      {actionNotice && <div className="ndr-action-notice">{actionNotice}</div>}

      {selectedNdrs.length > 0 && (
        <div className="ndr-selection-bar">
          <span>
            {selectedNdrs.length} shipment{selectedNdrs.length === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            className="btn btn-primary btn-small"
            onClick={() => setActionTarget({ ndrs: selectedNdrs, action: 'REATTEMPT' })}
          >
            Bulk Update
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setSelectedAwbs(new Set())}>
            Clear
          </button>
        </div>
      )}

      {loading || (pending && ndrs.length === 0) ? (
        <div className="loading-state">
          {pending ? 'Loading NDRs from ParcelX… ParcelX can take up to a minute to answer.' : 'Loading…'}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th className="ndr-check-cell">
                  <input
                    type="checkbox"
                    aria-label="Select all actionable shipments on this page"
                    disabled={pageSelectable.length === 0}
                    checked={pageAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSomeSelected && !pageAllSelected;
                    }}
                    onChange={togglePage}
                  />
                </th>
                <th aria-label="Details" />
                <th>Order</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Amount</th>
                <th>Products</th>
                <th>AWB</th>
                <th>NDR Remark</th>
                <th>Current Location &amp; Time</th>
                <th>Status</th>
                <th>Call</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} style={{ textAlign: 'center' }}>
                    {error ? 'Could not load ParcelX NDRs.' : 'No ParcelX NDRs match this filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((ndr) => {
                  const awb = String(ndr.awb_number);
                  const expanded = expandedAwb === awb;
                  const phone = ndr.customer_phone || '';
                  const selectable = selectableAwbs.has(awb);
                  const selected = selectedAwbs.has(awb);
                  const isCod = String(ndr.payment_mode || '').toUpperCase() === 'COD';
                  return (
                    <React.Fragment key={awb}>
                      <tr className={selected ? 'ndr-row-selected' : undefined}>
                        <td className="ndr-check-cell">
                          <input
                            type="checkbox"
                            aria-label={`Select shipment ${ndr.order_number || awb}`}
                            title={selectable ? undefined : FINAL_STATUS_TITLE}
                            disabled={!selectable}
                            checked={selected}
                            onChange={() => toggleRow(awb)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => setExpandedAwb(expanded ? null : awb)}
                            aria-label={expanded ? 'Hide details' : 'Show details'}
                            title={expanded ? 'Hide details' : 'Show address and shipment details'}
                          >
                            {expanded ? <ChevronDownIcon width={12} height={12} /> : <ChevronRightIcon width={12} height={12} />}
                          </button>
                        </td>
                        <td>
                          <div className="table-cell-strong">{ndr.order_number || '-'}</div>
                          <div className="stat-label">{formatDate(ndr.order_date)}</div>
                          {ndr.shipment_id != null && <div className="stat-label">PX #{ndr.shipment_id}</div>}
                        </td>
                        <td>
                          <div>{ndr.customer_name || '-'}</div>
                          {phone && <div className="stat-label">{phone}</div>}
                          {ndr.customer_alt_phone && <div className="stat-label">Alt {ndr.customer_alt_phone}</div>}
                        </td>
                        <td className="ndr-address-cell" title={formatAddress(ndr.address) || undefined}>
                          <div>{ndr.address?.line1 || '-'}</div>
                          {(ndr.address?.city || ndr.address?.pincode) && (
                            <div className="stat-label">
                              {[ndr.address.city, ndr.address.pincode].filter(Boolean).join(' - ')}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="table-cell-strong">{formatRupees(ndr.order_amount)}</div>
                          {ndr.payment_mode && (
                            <span className={`status ${isCod ? 'attempted' : 'answered'}`}>
                              {String(ndr.payment_mode).toUpperCase()}
                            </span>
                          )}
                          {isCod && ndr.cod_amount != null && Number(ndr.cod_amount) !== Number(ndr.order_amount) && (
                            <div className="stat-label">Collect {formatRupees(ndr.cod_amount)}</div>
                          )}
                        </td>
                        <td title={productNames(ndr) || undefined}>
                          <div>{ndr.products?.[0] || '-'}</div>
                          <div className="stat-label">
                            {ndr.products?.length > 1 ? `+${ndr.products.length - 1} more · ` : ''}
                            {ndr.total_quantity != null ? `Qty ${ndr.total_quantity}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className="ndr-provider-badge">ParcelX</span>
                          <div className="stat-label">{awb}</div>
                        </td>
                        <td>
                          {attemptsLabel(ndr) && <div className="table-cell-strong">{attemptsLabel(ndr)}</div>}
                          <div title={ndr.ndr_reason || undefined}>{ndr.ndr_reason || '-'}</div>
                          <div className="stat-label">{formatDateTime(ndr.ndr_time)}</div>
                        </td>
                        <td>
                          <div>{ndr.current_location || '-'}</div>
                          {ndr.current_event_time && <div className="stat-label">{formatDateTime(ndr.current_event_time)}</div>}
                        </td>
                        <td>
                          <span className={`status ${statusClass(ndr)}`}>{ndr.status_label || '-'}</span>
                        </td>
                        <td>
                          {phone ? (
                            <CallButton agents={knownAgents} customerNumber={normalizeNumber(phone)} onCallTriggered={() => {}} />
                          ) : (
                            <span title="No phone number on this shipment">-</span>
                          )}
                        </td>
                        <td>
                          <div className="ndr-row-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-small"
                              disabled={!selectable}
                              title={selectable ? undefined : FINAL_STATUS_TITLE}
                              onClick={() => setActionTarget({ ndrs: [ndr], action: 'REATTEMPT' })}
                            >
                              Re-attempt
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-small"
                              disabled={!selectable}
                              title={selectable ? 'Return to origin' : FINAL_STATUS_TITLE}
                              onClick={() => setActionTarget({ ndrs: [ndr], action: 'RTO' })}
                            >
                              RTO
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={2} />
                          <td colSpan={columnCount - 2}>
                            <ParcelxDetails ndr={ndr} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        totalItems={filteredNdrs.length}
      />

      {actionTarget && (
        <ParcelxActionModal
          ndrs={actionTarget.ndrs}
          initialAction={actionTarget.action}
          onClose={() => setActionTarget(null)}
          onDone={handleActionDone}
        />
      )}
    </>
  );
}

export default ParcelxNdr;
